import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { TOUR, scrollToId } from "@/data";

const reveal = {
    initial: { opacity: 0, y: 36 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};
const ease = [0.16, 1, 0.3, 1];

export default function LiveTour() {
    const [ready, setReady] = useState(false);
    const [active, setActive] = useState(null);
    const spot = TOUR.hotspots.find((h) => h.id === active);

    return (
        <section
            id="tour"
            className="bg-[#FBF9F5] px-6 py-28 lg:px-10 lg:py-40"
            data-testid="tour-section"
        >
            <div className="mx-auto max-w-[1400px]">
                <div className="flex flex-col justify-between gap-6 pb-10 lg:flex-row lg:items-end">
                    <div>
                        <motion.p
                            {...reveal}
                            className="font-mono2 text-[10px] uppercase tracking-[0.34em] text-[#6B6862]"
                            data-testid="tour-section-label"
                        >
                            Live experience
                        </motion.p>
                        <motion.h2
                            {...reveal}
                            transition={{ ...reveal.transition, delay: 0.08 }}
                            className="mt-4 font-display text-4xl font-light leading-[1.02] tracking-tight sm:text-6xl"
                            data-testid="tour-headline"
                        >
                            Don't read about it.
                            <br />
                            <span className="italic">Step inside.</span>
                        </motion.h2>
                    </div>
                    <motion.p
                        {...reveal}
                        className="max-w-sm text-base font-light leading-relaxed text-[#121211]/85 lg:text-lg"
                    >
                        A real 360° space. Drag to look around, then open the
                        points of interest below.
                    </motion.p>
                </div>

                <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2 bg-[#1C1C1A] px-7 py-4 lg:px-9" data-testid="tour-labels">
                    <p className="font-mono2 text-[10px] tracking-[0.3em] text-white/70" data-testid="tour-live-label">
                        LIVE · 360°
                    </p>
                    <p className="font-display text-xl italic text-[#FAF8F5] sm:text-2xl" data-testid="tour-title">
                        {TOUR.title}
                    </p>
                </div>
                <motion.div
                    initial={{ clipPath: "inset(8% 4% 8% 4%)", opacity: 0 }}
                    whileInView={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
                    viewport={{ once: true, margin: "-120px" }}
                    transition={{ duration: 1.3, ease }}
                    className="relative bg-[#1C1C1A]"
                    data-testid="tour-frame"
                >
                    <div className="relative aspect-[4/5] w-full sm:aspect-[16/10] lg:aspect-[21/10]">
                        <iframe
                            title="LILIKOOP live 360° walkthrough"
                            src={TOUR.embedUrl}
                            width="100%"
                            height="640"
                            frameBorder="0"
                            scrolling="no"
                            onLoad={() => setReady(true)}
                            allow="xr-spatial-tracking; gyroscope; accelerometer"
                            allowFullScreen
                            className="absolute inset-0 h-full w-full border-0"
                            data-testid="tour-viewer"
                        />
                    </div>

                    {!ready && (
                        <div
                            className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#1C1C1A]"
                            data-testid="tour-loading"
                        >
                            <p className="pulse-soft font-mono2 text-[10px] uppercase tracking-[0.34em] text-white/50">
                                Opening the space
                            </p>
                        </div>
                    )}

                    <div className="pointer-events-none absolute left-4 top-4 h-3 w-3 border-l border-t border-white/60" />
                    <div className="pointer-events-none absolute right-4 top-4 h-3 w-3 border-r border-t border-white/60" />
                    <div className="pointer-events-none absolute bottom-4 left-4 h-3 w-3 border-b border-l border-white/60" />
                    <div className="pointer-events-none absolute bottom-4 right-4 h-3 w-3 border-b border-r border-white/60" />

                </motion.div>

                <div
                    className="mt-6 grid gap-px border-y border-[#121211]/10 sm:grid-cols-2 lg:grid-cols-5"
                    data-testid="tour-hotspots"
                >
                    {TOUR.hotspots.map((h) => (
                        <button
                            key={h.id}
                            data-interactive
                            data-testid={`tour-hotspot-${h.id}`}
                            aria-expanded={active === h.id}
                            onClick={() => setActive(active === h.id ? null : h.id)}
                            className={`group flex items-center gap-4 px-2 py-5 text-left transition-colors duration-300 lg:px-4 ${active === h.id ? "bg-[#121211] text-[#FBF9F5]" : "hover:bg-[#121211]/[0.04]"}`}
                        >
                            <span className="relative flex h-7 w-7 shrink-0 items-center justify-center">
                                <span className={`hs-halo absolute inset-0 rounded-full border ${active === h.id ? "border-white/50" : "border-[#121211]/40"}`} />
                                <span className={`h-2 w-2 rounded-full transition-transform duration-300 group-hover:scale-125 ${active === h.id ? "bg-[#FBF9F5]" : "bg-[#121211]"}`} />
                            </span>
                            <span className="min-w-0">
                                <span className={`block font-mono2 text-[9px] tracking-[0.3em] ${active === h.id ? "text-white/50" : "text-[#6B6862]"}`}>
                                    {h.index}
                                </span>
                                <span className="block font-display text-lg font-light leading-tight">
                                    {h.title}
                                </span>
                            </span>
                        </button>
                    ))}
                </div>
                <p data-testid="tour-examples-disclaimer" className="mt-3 text-[10px] font-light leading-relaxed text-[#6B6862]">
                    Illustrative examples of interactive content within the experience.
                </p>

                <AnimatePresence>
                        {spot && (
                            <motion.div
                                key={spot.id}
                                initial={{ y: 24, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 24, opacity: 0 }}
                                transition={{ duration: 0.6, ease }}
                                className="relative ml-auto mt-6 max-w-md bg-[#FBF9F5] p-6 text-[#121211] lg:p-7"
                                data-testid="tour-hotspot-card"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <p className="font-mono2 text-[9px] uppercase tracking-[0.3em] text-[#6B6862]">
                                        {spot.index} · Point of interest
                                    </p>
                                    <button
                                        data-interactive
                                        data-testid="tour-hotspot-close"
                                        onClick={() => setActive(null)}
                                        aria-label="Close"
                                        className="-mr-1 -mt-1 rounded-full p-1 transition-colors duration-300 hover:bg-[#121211]/10"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                <h4 className="mt-3 font-display text-2xl font-light leading-tight sm:text-3xl">
                                    {spot.title}
                                </h4>
                                <p className="mt-3 text-sm font-light leading-relaxed text-[#121211]/75">
                                    {spot.description}
                                </p>
                                <button
                                    data-interactive
                                    data-testid="tour-hotspot-cta"
                                    onClick={() => {
                                        if (spot.action === "contact") scrollToId("contact");
                                        else setActive(null);
                                    }}
                                    className="project-cta group mt-5 inline-flex items-center gap-3 rounded-full bg-[#121211] px-5 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.24em] text-[#FBF9F5]"
                                >
                                    {spot.cta}
                                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.26em] text-[#6B6862]">
                    {TOUR.location}
                </p>
            </div>
        </section>
    );
}
